import React from 'react';
import { Link } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import LegalHero from './LegalHero.js';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';

const FLARE_RED = '#c4151c';

function PrivacyContact() {
  const brand = useSiteBrand();
  const email = 'digital@flare.pk';
  const address = brand.officeHqAddress || '2nd Floor, Digital Flare Network, Main Boulevard, Gulberg III, Lahore, 54660, Pakistan';
  return (
    <div className="space-y-1 text-gray-600 leading-relaxed">
      <p><strong>Data Protection Officer:</strong> Flare Magazine Editorial Office</p>
      <p>Address: {address}</p>
      <p>
        Email:{' '}
        <a href={`mailto:${email}`} className="font-semibold hover:underline" style={{ color: FLARE_RED }}>{email}</a>
        {' '}&nbsp;|&nbsp; Phone: <strong style={{ color: FLARE_RED }}>{brand.phone}</strong> (Mon–Sat, 10 AM – 6 PM PKT)
      </p>
    </div>
  );
}

const SECTION = [
  {
    title: '1. Information We Collect',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li><strong>Account &amp; Order Data:</strong> Full name, billing/shipping address, email address, mobile/WhatsApp number, CNIC (for COD verification when required), and order history.</li>
        <li><strong>Payment Data:</strong> Transaction IDs, payment method, and timestamp. We NEVER store debit/credit card numbers or full wallet PINs on our servers — all sensitive card processing is handled by licensed Pakistani PCI-DSS compliant gateways (JazzCash, EasyPaisa, HBL, MCB).</li>
        <li><strong>Newsletter &amp; Marketing:</strong> Name, email address and mobile number submitted via the &ldquo;Never miss a new issue&rdquo; footer form, Contact sidebar Flash Newsletter box, or checkout opt-in.</li>
        <li><strong>Device &amp; Usage:</strong> Browser type, device OS, IP address (anonymized after 30 days), and pages visited, collected via standard HTTP logs and Google Analytics 4 with IP anonymization enabled.</li>
      </ul>
    ),
  },
  {
    title: '2. How We Use Your Information',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li>Fulfill and deliver your magazine orders (printed copies) and provide free digital PDF access where available.</li>
        <li>Send order confirmations, dispatch SMS/email, delivery updates, and invoice/receipt copies.</li>
        <li>Notify subscribers when a new Flare issue is published (Flash Newsletter — unsubscribe anytime with one click).</li>
        <li>Personalize the homepage grid (e.g., show your favorite categories first) and save your cart between sessions.</li>
        <li>Prevent fraud, verify Cash-on-Delivery requests, and resolve disputes with courier partners.</li>
        <li>Comply with Pakistani tax and audit laws (sales invoices retained for 6 years per FBR rules).</li>
      </ul>
    ),
  },
  {
    title: '3. Payment &amp; Billing Data Security',
    body: (
      <p className="text-gray-600 leading-relaxed">
        All payment pages are served over HTTPS TLS 1.3. Card and mobile wallet numbers are tokenized by our acquiring banks BEFORE they reach our application layer. We keep only the last 4 digits, transaction ID, gateway response code, and timestamp. Billing addresses are stored in encrypted-at-rest columns (AES-256) inside our Supabase/Postgres database and are never exported to CSV except by the super-admin for reconciliation with a 2FA-audited export log.
      </p>
    ),
  },
  {
    title: '4. Newsletter &amp; Marketing Communications',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>When you sign up to our Flash Newsletter (footer, Contact page, or checkout tick-box) we will send you:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li>One announcement email/SMS every time a new Flare Magazine issue goes live (approx. 1× per month).</li>
          <li>Occasional (max 2× per quarter) subscriber-only discounts for annual plans or bundle offers.</li>
        </ul>
        <p>Every message contains a one-click unsubscribe link or a STOP/UNSUB SMS reply code. Requests are honored within 24 hours. We do NOT sell, rent, or share your email/phone with any third-party list broker or unrelated advertiser.</p>
      </div>
    ),
  },
  {
    title: '5. Cookies &amp; Analytics',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li><strong>Necessary cookies only:</strong> We drop a small first-party session cookie (<code>cart_session</code>) to keep items in your cart and a CSRF token cookie to protect your forms from cross-site abuse.</li>
        <li><strong>Analytics:</strong> Google Analytics 4 (anonymized IPs, EU/PK compliant) and Plausible Lite privacy-friendly pageviews. No cross-site tracking, no advertising pixels.</li>
        <li><strong>Third-party embeds:</strong> The Contact page may load Google Maps for our Lahore office location — that embed is governed by Google&rsquo;s own Privacy Policy.</li>
      </ul>
    ),
  },
  {
    title: '6. Third-Party Service Providers We Share Data With',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>We only disclose strictly-needed fields to these sub-processors, all bound by written DPAs:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li><strong>Courier partners</strong> (TCS, Leopard, Pakistan Post): Name, delivery address, phone number, weight, and declared value of printed magazine shipments.</li>
          <li><strong>Payment gateways</strong> (JazzCash, EasyPaisa, 1LINK): Order number, amount, and buyer email for receipt delivery.</li>
          <li><strong>Transactional email/SMS</strong> (Brevo/Sendinblue, Jazz Bulk SMS): Name, email, phone, and message template variables only.</li>
          <li><strong>Hosting &amp; database</strong> (Supabase / AWS ap-south-1 Mumbai region): Encrypted-at-rest storage; staff access logged &amp; 2FA-only.</li>
        </ul>
      </div>
    ),
  },
  {
    title: '7. Data Retention Periods',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li><strong>Active orders &amp; invoices:</strong> 6 years from tax year-end (mandatory FBR Pakistan record-keeping).</li>
        <li><strong>Newsletter subscribers:</strong> Until the user unsubscribes; if no opens/clicks for 24 consecutive months, records are auto-archived then deleted after a further 90-day grace window.</li>
        <li><strong>Contact request forms:</strong> 12 months from case closure.</li>
        <li><strong>Anonymous analytics:</strong> 14 months (Google Analytics retention setting).</li>
      </ul>
    ),
  },
  {
    title: '8. Your Rights (Punjab Data Protection Act 2024 Compliant)',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>As a Pakistani data subject you can, free of charge, exercise the following rights by writing to us at <a href="mailto:digital@flare.pk" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>digital@flare.pk</a>:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li><strong>Right of Access</strong> — request a machine-readable JSON/CSV copy of every record we hold about you.</li>
          <li><strong>Right to Rectification</strong> — correct a misspelled name, outdated address, or wrong phone number.</li>
          <li><strong>Right to Erasure (&ldquo;be forgotten&rdquo;)</strong> — delete your account &amp; personal data (we will refuse only if the law forces us to keep it, e.g., unpaid tax invoices).</li>
          <li><strong>Right to Object</strong> — opt out of direct marketing at any time.</li>
          <li><strong>Right to Withdraw Consent</strong> — where processing was based on consent, not on contract or legal obligation.</li>
        </ul>
        <p>We respond within <strong>15 working days</strong>. If you are unhappy with our response you can escalate to the Punjab Data Protection Commission (PDPC), Lahore.</p>
      </div>
    ),
  },
  {
    title: '9. Children&rsquo;s Privacy',
    body: (
      <p className="text-gray-600 leading-relaxed">
        Flare Magazine is a general-interest publication not specifically directed at children under 13. We do not knowingly collect personal data from anyone under 13; if a parent informs us such data was collected we will permanently delete it within 72 hours — please email <a href="mailto:digital@flare.pk" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>digital@flare.pk</a> with the subject line &ldquo;Child data removal&rdquo;.
      </p>
    ),
  },
  {
    title: '10. Changes to This Privacy Policy',
    body: (
      <p className="text-gray-600 leading-relaxed">
        Material changes (e.g., a new data processor or a new purpose) will be posted on this page with a new &ldquo;Last updated&rdquo; date and, for existing active subscribers, notified by a single email at least 14 days before they take effect. Your continued use of digital.flare.pk after the effective date constitutes acceptance of the revised policy.
      </p>
    ),
  },
  {
    title: '11. Contact for Privacy Issues',
    body: <PrivacyContact />,
  },
];

export default function ShopPrivacyPolicySection() {
  return (
    <ShopLayout pageTitle="Privacy Policy" currentPage="privacy">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <LegalHero
          kicker="Legal · digital.flare.pk"
          title="Privacy Policy"
          updated={<>Last updated: <span className="text-gray-700">January 15, 2026</span> · Effective: January 20, 2026</>}
          image="/legal/privacy.png"
          accent={FLARE_RED}
        />

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-8">
          <p className="text-gray-600 leading-relaxed border-l-4 pl-4" style={{ borderColor: FLARE_RED }}>
            At <strong>Flare Magazine</strong> (operated by <em>Digital Flare Network (Private) Limited</em>, Lahore, Pakistan), your privacy is non-negotiable. This policy describes, in plain English, exactly what personal data we collect when you use <code>digital.flare.pk</code>, why we collect it, how long we keep it, who we share it with, and the rights you have under the <strong>Punjab Data Protection Act, 2024</strong> and the <strong>Electronic Transactions Ordinance, 2002</strong>.
          </p>

          {SECTION.map((s) => (
            <section key={s.title} id={s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')} className="scroll-mt-24">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">
                {s.title}
              </h2>
              {s.body}
            </section>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 shadow-[0_8px_20px_-8px_rgba(196,21,28,0.4)]"
            style={{ backgroundColor: FLARE_RED }}
          >
            Still have questions? Talk to our team →
          </Link>
        </div>
      </div>
    </ShopLayout>
  );
}
